import { isEmpty } from 'ramda';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { CreateVideoClickTarget } from '../../constants/Bi';
import { PageTabsNames } from '../../constants/PageTabsNames';
import {
  PromoteFrameLocationTarget,
  PromoteFramePostMessageData,
} from '../../constants/PromoteFrameData';
import { FedopsInteractions, fedopsLogger } from '../../services/fedopsLogger';
import { sentryClient } from '../../services/sentryClient';
import { checkPostMessageSender } from '../../utils/checkPostMessageSender';
import { useTrackCreateVideoClick } from '../../utils/hooks/bi/useTrackCreateVideoClick';
import { useTrackEditTemplateClick } from '../../utils/hooks/bi/useTrackEditTemplateClick';
import { useExperiments } from '../../utils/hooks/useExperiments';
import { useFrameSrc } from '../../utils/hooks/useFrameSrc/';
import { useVideoMaker } from '../../utils/hooks/useVideoMaker';
import { Orientations } from '../../utils/hooks/useVideoMaker/videoMaker.types';
import { useVideoMakerTemplates } from '../../utils/hooks/useVideoMakerTemplates';
import styles from '../HomePage/HomePage.scss';
import { IOuter } from './FrameContent.types';

export const FrameContent: FC<IOuter> = () => {
  useEffect(() => {
    fedopsLogger.interactionStarted(FedopsInteractions.PromoteIframeLoaded);
  }, []);

  const [iframeHeight, setFrameHeight] = useState(0);

  const frameSrc = useFrameSrc();

  const { getTemplateData, findTemplateByType } = useVideoMakerTemplates();
  const { openVideoMaker } = useVideoMaker();
  const { trackVideoMakerCreateVideoClick } = useTrackCreateVideoClick();
  const { trackEditTemplateClick } = useTrackEditTemplateClick();

  const { isExperimentEnabled } = useExperiments();

  const openVideoMakerTemplate = useCallback(
    ({
      type = 'create-new',
      orientation = Orientations.Landscape,
      location,
      openerParams = {},
    }) => {
      if (location === PromoteFrameLocationTarget.TopCard) {
        trackVideoMakerCreateVideoClick({
          tabName: PageTabsNames.Templates,
          target: CreateVideoClickTarget.GetStarted,
        });
      } else if (location === PromoteFrameLocationTarget.Template) {
        trackEditTemplateClick({
          format: orientation,
          entityId: type,
        });
      }

      let requiredTemplate;
      try {
        requiredTemplate = isEmpty(openerParams)
          ? findTemplateByType(type)
          : JSON.parse(openerParams);
      } catch (error) {
        sentryClient.captureException(error);
        requiredTemplate = findTemplateByType(type);
      }

      const templateData = getTemplateData({
        ...requiredTemplate,
        orientation,
      });

      openVideoMaker(templateData);
    },
    [
      findTemplateByType,
      getTemplateData,
      openVideoMaker,
      trackVideoMakerCreateVideoClick,
      trackEditTemplateClick,
      isExperimentEnabled,
      sentryClient,
    ],
  );

  const handleReceiveMessage = useCallback(
    (event) => {
      const isMessageFromPromoteFrame = checkPostMessageSender(event);
      if (!isMessageFromPromoteFrame) {
        return;
      }

      const { data } = event;

      if (
        data &&
        data.action === PromoteFramePostMessageData.PromoteFrameLoadedAction
      ) {
        fedopsLogger.interactionEnded(FedopsInteractions.PromoteIframeLoaded);
      }

      if (data.siteHeight) {
        setFrameHeight(data.siteHeight);
      } else if (
        data.action &&
        data.action === PromoteFramePostMessageData.OpenVideoMakerAction
      ) {
        openVideoMakerTemplate(data);
      }
    },
    [setFrameHeight, openVideoMakerTemplate, checkPostMessageSender],
  );

  useEffect(() => {
    window.addEventListener('message', handleReceiveMessage, false);

    return () => {
      window.removeEventListener('message', handleReceiveMessage);
    };
  }, [handleReceiveMessage]);

  return (
    <>
      <iframe
        title="video-maker"
        height={iframeHeight || 1401}
        width={984}
        className={styles.iframeContainer}
        src={frameSrc}
        scrolling="no"
      />
    </>
  );
};
